import React, { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Home, 
  Users, 
  CreditCard, 
  TrendingUp, 
  Settings, 
  Menu, 
  X,
  LogOut,
  User,
  Plane,
  UserPlus,
  Tag,
  Bus,
  FileText,
  Percent,
  Luggage
} from 'lucide-react';
import styles from './Layout.module.css';
import logo from '../assets/images/logo.svg';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  roles: ('admin' | 'emissor' | 'gestor' | 'reader')[];
}

const navItems: NavItem[] = [
  {
    path: '/dashboard',
    label: 'Dashboard',
    icon: <Home className={styles.navIcon} />,
    roles: ['admin', 'emissor', 'gestor', 'reader'],
  },
  {
    path: '/users',
    label: 'Gestão de Usuários',
    icon: <Users className={styles.navIcon} />,
    roles: ['admin'],
  },
  {
    path: '/emissions',
    label: 'Emissão de Passagens',
    icon: <CreditCard className={styles.navIcon} />,
    roles: ['admin', 'emissor', 'reader'],
  },
  {
    path: '/bus-emissions',
    label: 'Emissão de Ônibus',
    icon: <Bus className={styles.navIcon} />,
    roles: ['admin', 'emissor', 'reader'],
  },
  {
    path: '/quotes',
    label: 'Cotação Manual',
    icon: <Plane className={styles.navIcon} />,
    roles: ['admin', 'emissor'],
  },
  {
    path: '/bus-manual-checkout',
    label: 'Checkout Manual Ônibus',
    icon: <Bus className={styles.navIcon} />,
    roles: ['admin', 'emissor'],
  },
  {
    path: '/coupons',
    label: 'Cupons de Desconto',
    icon: <Tag className={styles.navIcon} />,
    roles: ['admin'],
  },
  {
    path: '/installment-rates',
    label: 'Taxas de Parcelamento',
    icon: <Percent className={styles.navIcon} />,
    roles: ['admin'],
  },
  {
    path: '/baggage-rules',
    label: 'Regras de Bagagem',
    icon: <Luggage className={styles.navIcon} />,
    roles: ['admin', 'gestor'],
  },
  {
    path: '/markups',
    label: 'Gestão de Markups',
    icon: <TrendingUp className={styles.navIcon} />,
    roles: ['admin', 'gestor'],
  },
  {
    path: '/mileage-cards',
    label: 'Cartões de Milhas',
    icon: <CreditCard className={styles.navIcon} />,
    roles: ['admin'],
  },
  {
    path: '/airports',
    label: 'Gestão de Aeroportos',
    icon: <Plane className={styles.navIcon} />,
    roles: ['admin'],
  },
  {
    path: '/leads',
    label: 'Leads',
    icon: <UserPlus className={styles.navIcon} />,
    roles: ['admin', 'emissor', 'gestor', 'reader'],
  },
  {
    path: '/settings',
    label: 'Configurações',
    icon: <Settings className={styles.navIcon} />,
    roles: ['admin'],
  },
  {
    path: '/terms',
    label: 'Termos de Uso',
    icon: <FileText className={styles.navIcon} />,
    roles: ['admin'],
  },
];

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, role, logout } = useAuth();
  const location = useLocation();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const handleLogout = () => {
    logout();
  };

  const getVisibleNavItems = () => {
    if (!role) return [];
    return navItems.filter(item => 
      item.roles.some(requiredRole => role[requiredRole as keyof typeof role])
    );
  };

  const getUserRole = () => {
    if (!role) return '';
    if (role.admin) return 'Administrador';
    if (role.gestor) return 'Gestor';
    if (role.emissor) return 'Emissor';
    if (role.reader) return 'Leitor';
    return 'Usuário';
  };

  const getPageTitle = () => {
    const currentItem = navItems.find(item => item.path === location.pathname);
    return currentItem?.label || 'Dashboard';
  };


  return (
    <div className={styles.layout}>
      <div 
        className={`${styles.overlay} ${sidebarOpen ? styles.overlayVisible : ''}`}
        onClick={closeSidebar}
      />

      <aside className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarVisible : ''}`}>
        <div className={styles.sidebarHeader}>
          <img src={logo} alt="999 Viagens" className={styles.sidebarLogo} />
         
        </div>

        <nav className={styles.sidebarNav}>
          {getVisibleNavItems().map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`${styles.navItem} ${
                location.pathname === item.path ? styles.navItemActive : ''
              }`}
              onClick={closeSidebar}
            >
              {item.icon}
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className={styles.sidebarFooter}>
          <div className={styles.userInfo}>
            <div className={styles.userAvatar}>
              <User size={20} />
            </div>
            <div className={styles.userDetails}>
              <p className={styles.userName}>
                {user?.username} {user?.lastname}
              </p>
              <p className={styles.userRole}>
                {getUserRole()}
              </p>
            </div>
          </div>
          <button onClick={handleLogout} className={styles.logoutButton}>
            <LogOut size={14} />
            Sair
          </button>
        </div>
      </aside>

      <main className={`${styles.mainContent} ${sidebarOpen ? styles.mainContentExpanded : ''}`}>
        <header className={styles.topBar}>
          <div className={styles.topBarLeft}>
            <button onClick={toggleSidebar} className={styles.menuToggle}>
              {sidebarOpen ? <X /> : <Menu />}
            </button>
            <h1 className={styles.pageTitle}>{getPageTitle()}</h1>
          </div>
          <div className={styles.topBarRight}>
            <div className={styles.userInfo}>
              <span>Olá, {user?.username}!</span>
            </div>
          </div>
        </header>

        <div className={styles.contentArea}>
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;